// Archive — settled markets pulled once from mongo via /markets/past/recent,
// grouped one card per event: header = logos + score + league/date meta,
// lines = markets with sparkline + final price.

import { useState } from 'react';
import { groupArchiveByEvent, type SettledEntry } from '../feed';
import { EventGroupCard, MatchHeader, SportLeague } from './event-group';
import { Sparkline } from './sparkline';
import { RibbonCard } from './status-ribbon';

const COLLAPSED = 12;

const fmtDate = (ms: number) =>
  ms > 0
    ? new Date(ms).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year: '2-digit',
      })
    : '';

export function ArchiveList({ entries }: { entries: SettledEntry[] }) {
  const [expanded, setExpanded] = useState(false);
  const groups = groupArchiveByEvent(entries);
  const shown = expanded ? groups : groups.slice(0, COLLAPSED);
  return (
    <div className="flex flex-col gap-2">
      {shown.map(({ event, markets }) => (
        <RibbonCard key={event.eventId} status="finished">
          <EventGroupCard
            header={
              <MatchHeader
                event={event}
                score={event.score}
                meta={
                  <>
                    <SportLeague sport={event.sport} league={event.league} />
                    <span aria-hidden="true">·</span>
                    <span className="tabular-nums">
                      {fmtDate(event.startTime)}
                    </span>
                    <span aria-hidden="true">·</span>
                    <span>
                      {markets.length}{' '}
                      {markets.length === 1 ? 'market' : 'markets'}
                    </span>
                  </>
                }
              />
            }
          >
            {markets.map((market) => (
              <div
                key={market.index}
                className="flex items-center gap-3 py-2 text-xs"
              >
                <p className="min-w-0 flex-1 truncate opacity-80">
                  {market.displayName || market.name}
                </p>
                <Sparkline
                  points={market.history}
                  direction={market.priceDirection}
                />
                <span className="w-14 shrink-0 text-right tabular-nums opacity-80">
                  {market.price === null ? '—' : market.price.toFixed(2)}
                </span>
              </div>
            ))}
          </EventGroupCard>
        </RibbonCard>
      ))}
      {groups.length > COLLAPSED && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="self-start text-xs opacity-60 underline underline-offset-2 hover:opacity-100"
        >
          {expanded ? 'Show less' : `+${groups.length - COLLAPSED} more events`}
        </button>
      )}
    </div>
  );
}
